import { useState, useEffect, useMemo } from 'react';
import {
    Grid,
    FormControl,
    Select,
    MenuItem,
    FormHelperText,
    Box,
    Typography,
    ListSubheader,
    InputLabel,
    Autocomplete,
    TextField,
} from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    useBusinessCentralProductsQuery,
    useProductCatalogQuery,
    useCreateProductCatalogMutation,
    useUpdateProductCatalogMutation,
} from '@features/redux/apis/productCatalog';
import { useAlert } from '@hooks/useAlert';

import type { ProductCatalog } from '@/types/productCatalog';
import { ProductCatalogStatus, PRODUCT_CATALOG_STATUS_OPTIONS } from '@/types/productCatalog';
import { type BillingType, CUT_FEE_MAP } from '@consts/company';
import {
    colors,
    typography as typographyTokens,
    radii,
    EntityPageLayout,
    EntityPageHeader,
    InfoCard,
    DraftSelectField,
    ReadOnlyField,
    Button,
} from '@components/design-system';

// Valid characters for short code: 0123456789ABCDEFGHJKLMNPQRTUVWXYZ
// Missing: I, O, S (commonly confused characters)
const VALID_SHORT_CODE_CHARS = '0123456789ABCDEFGHJKLMNPQRTUVWXYZ';
const SHORT_CODE_LENGTH = 2;

const PRODUCT_LINE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
    Original: { bg: colors.primary[50], text: colors.primary[700], border: colors.primary[200] },
    Fusion: { bg: colors.success.light, text: colors.success.dark, border: '#A7F3D0' },
    'Style Skins': { bg: colors.warning.light, text: colors.warning.dark, border: '#FDE68A' },
};

const PRODUCT_LINE_ORDER = ['Original', 'Fusion', 'Style Skins'];

interface ProductCatalogEditPanelProps {
    productCatalog?: ProductCatalog;
    existingProducts: ProductCatalog[];
    onClose: () => void;
    onSave: () => void;
}

interface DialogState {
    ShortCode: string;
    ProductSKU: string;
    ProductName: string;
    ProductLine: string;
    InvoiceName: string;
    CutProfile: number | null;
    SuccessCutSKU: string;
    WarrantyCutSKU: string;
    CutPlanID: number;
    SheetID: number;
    JobName: string;
    Status: string;
    [key: string]: string | number | null;
}

interface ProductOption {
    ProductSKU: string;
    ProductName: string;
    ProductLine: string;
    InvoiceName: string;
    CutProfile: number | null;
    CutProfileSKU: string | null;
    CutPlanID: number;
    SheetID: number;
    SheetName?: string;
    JobName: string;
}

// Validate short code characters
const validateShortCodeChar = (char: string): boolean => {
    return VALID_SHORT_CODE_CHARS.includes(char.toUpperCase());
};

// Filter and uppercase short code input
const sanitizeShortCode = (input: string): string => {
    return input
        .toUpperCase()
        .split('')
        .filter((char) => validateShortCodeChar(char))
        .slice(0, SHORT_CODE_LENGTH)
        .join('');
};

export const ProductCatalogEditPanel = ({
    productCatalog,
    existingProducts,
    onClose,
    onSave,
}: ProductCatalogEditPanelProps) => {
    const { setAlert } = useAlert();

    const isEditMode = !!productCatalog;

    // Load products data for dropdowns
    const { data: productsData } = useBusinessCentralProductsQuery({});

    // Load existing product catalog if editing
    const { data: existingProduct, isLoading: isLoadingProduct } = useProductCatalogQuery(
        { productSKU: productCatalog?.ProductSKU || '' },
        { skip: !productCatalog }
    );

    const [createProduct, { isLoading: isCreating }] = useCreateProductCatalogMutation();
    const [updateProduct, { isLoading: isUpdating }] = useUpdateProductCatalogMutation();

    const initialState: DialogState = {
        ShortCode: '',
        ProductSKU: '',
        ProductName: '',
        ProductLine: '',
        InvoiceName: '',
        CutProfile: null,
        SuccessCutSKU: '',
        WarrantyCutSKU: '',
        CutPlanID: 0,
        SheetID: 0,
        JobName: 'Pull_Tab',
        Status: ProductCatalogStatus.Production,
    };

    const [state, setState] = useState<DialogState>(initialState);
    const [shortCodeTouched, setShortCodeTouched] = useState(false);

    const isLoading = isLoadingProduct || isCreating || isUpdating;

    // Get existing short codes (excluding current product if editing)
    const existingShortCodes = useMemo(() => {
        return existingProducts
            .filter((p) => !productCatalog || p.ProductSKU !== productCatalog.ProductSKU)
            .map((p) => p.ShortCode.toUpperCase());
    }, [existingProducts, productCatalog]);

    // Check if short code is a duplicate
    const isShortCodeDuplicate = useMemo(() => {
        if (!state.ShortCode || state.ShortCode.length !== SHORT_CODE_LENGTH) return false;
        return existingShortCodes.includes(state.ShortCode.toUpperCase());
    }, [state.ShortCode, existingShortCodes]);

    // Short code validation
    const shortCodeError = useMemo(() => {
        if (!shortCodeTouched && !state.ShortCode) return '';
        if (state.ShortCode.length === 0) return 'Short code is required';
        if (state.ShortCode.length !== SHORT_CODE_LENGTH) return `Short code must be exactly ${SHORT_CODE_LENGTH} characters`;
        if (isShortCodeDuplicate) return 'This short code is already in use';
        return '';
    }, [state.ShortCode, shortCodeTouched, isShortCodeDuplicate]);

    // Get existing product SKUs (to filter from dropdown)
    const existingProductSKUs = useMemo(() => {
        return existingProducts.map((p) => p.ProductSKU);
    }, [existingProducts]);

    // Filter and group products for dropdown
    const groupedProductOptions = useMemo((): Record<string, ProductOption[]> => {
        if (!productsData?.products) return { Original: [], Fusion: [], 'Style Skins': [], Other: [] };

        // Filter out already added products (unless editing)
        const availableProducts = productsData.products.filter(
            (p) => !existingProductSKUs.includes(p.ProductSKU) || (isEditMode && p.ProductSKU === productCatalog?.ProductSKU)
        ) as ProductOption[];

        // Group by product line
        const grouped: Record<string, ProductOption[]> = {
            Original: [],
            Fusion: [],
            'Style Skins': [],
            Other: [],
        };

        availableProducts.forEach((product) => {
            const line = PRODUCT_LINE_ORDER.includes(product.ProductLine) ? product.ProductLine : 'Other';
            grouped[line].push(product);
        });

        // Sort products within each group
        Object.keys(grouped).forEach((line) => {
            grouped[line].sort((a, b) => a.ProductName.localeCompare(b.ProductName));
        });

        return grouped;
    }, [productsData, existingProductSKUs, isEditMode, productCatalog]);

    // Initialize form with existing product data
    useEffect(() => {
        if (existingProduct) {
            setState({
                ShortCode: existingProduct.ShortCode,
                ProductSKU: existingProduct.ProductSKU,
                ProductName: existingProduct.ProductName,
                ProductLine: existingProduct.ProductLine,
                InvoiceName: existingProduct.InvoiceName,
                CutProfile: existingProduct.CutProfile ?? null,
                SuccessCutSKU: existingProduct.SuccessCutSKU,
                WarrantyCutSKU: existingProduct.WarrantyCutSKU,
                CutPlanID: existingProduct.CutPlanID,
                SheetID: existingProduct.SheetID ?? 0,
                JobName: existingProduct.JobName || 'Pull_Tab',
                Status: existingProduct.Status || ProductCatalogStatus.Production,
            });
        } else {
            setState(initialState);
        }
    }, [existingProduct]);

    const handleSubmit = async () => {
        if (!state.ShortCode || !state.ProductSKU || !state.SuccessCutSKU || !state.WarrantyCutSKU || !state.JobName) {
            setAlert('Please fill in all required fields', 'error', 5000);
            return;
        }

        if (state.ShortCode.length !== SHORT_CODE_LENGTH) {
            setAlert(`Short Code must be exactly ${SHORT_CODE_LENGTH} characters`, 'error', 5000);
            return;
        }

        if (isShortCodeDuplicate) {
            setAlert('This short code is already in use', 'error', 5000);
            return;
        }

        const selectedProduct = productsData?.products.find((p) => p.ProductSKU === state.ProductSKU);
        if (!selectedProduct) {
            setAlert('Invalid product selection', 'error', 5000);
            return;
        }

        const payload = {
            ShortCode: state.ShortCode,
            ProductSKU: state.ProductSKU,
            ProductName: selectedProduct.ProductName,
            ProductLine: selectedProduct.ProductLine,
            InvoiceName: selectedProduct.InvoiceName,
            CutProfile: selectedProduct.CutProfile ?? null,
            CutProfileSKU: selectedProduct.CutProfileSKU ?? null,
            SuccessCutSKU: state.SuccessCutSKU,
            WarrantyCutSKU: state.WarrantyCutSKU,
            CutPlanID: selectedProduct.CutPlanID,
            SheetID: selectedProduct.SheetID,
            JobName: state.JobName,
            Status: state.Status as ProductCatalogStatus,
        };

        try {
            if (existingProduct) {
                await updateProduct({ productCatalog: payload }).unwrap();
                setAlert('Product catalog updated successfully!', 'success', 5000);
            } else {
                await createProduct({ productCatalog: payload }).unwrap();
                setAlert('Product catalog created successfully!', 'success', 5000);
            }
            onSave();
        } catch {
            setAlert('Failed to save product catalog. Please try again.', 'error', 5000);
        }
    };

    // Unique job names from existing product catalogs for autocomplete
    const jobNameOptions = useMemo(() => {
        const names = new Set(existingProducts.map((p) => p.JobName).filter(Boolean));
        return Array.from(names).sort();
    }, [existingProducts]);

    const isPrepaid = state.CutPlanID === 1;
    const planKey = isPrepaid ? 'prepaid' : 'postpaid';

    const successSKUOptions = productsData?.successSKUs?.[planKey] || [];
    const warrantySKUOptions = productsData?.warrantySKUs?.[planKey] || [];

    const successSKUOptionsForm = successSKUOptions.map((skuOption) => ({
        value: skuOption.SKU,
        label: `${skuOption.SKU} - ${skuOption.Name}`,
    }));

    const warrantySKUOptionsForm = warrantySKUOptions.map((warrantyOption) => ({
        value: warrantyOption.SKU,
        label: `${warrantyOption.SKU} - ${warrantyOption.Name}`,
    }));

    // Update product name when product SKU changes (only in add mode)
    useEffect(() => {
        if (state.ProductSKU && !isEditMode) {
            const selectedProduct = productsData?.products.find((p) => p.ProductSKU === state.ProductSKU);
            if (selectedProduct) {
                setState((prev) => ({
                    ...prev,
                    InvoiceName: selectedProduct.InvoiceName,
                    ProductName: selectedProduct.ProductName,
                    ProductLine: selectedProduct.ProductLine,
                    CutProfile: selectedProduct.CutProfile ?? null,
                    CutPlanID: selectedProduct.CutPlanID,
                    SheetID: selectedProduct.SheetID,
                    JobName: selectedProduct.JobName,
                    // Reset SKU selections when CutPlanID changes (prepaid vs postpaid have different options)
                    SuccessCutSKU: selectedProduct.CutPlanID !== prev.CutPlanID ? '' : prev.SuccessCutSKU,
                    WarrantyCutSKU: selectedProduct.CutPlanID !== prev.CutPlanID ? '' : prev.WarrantyCutSKU,
                }));
            }
        }
    }, [state.ProductSKU, productsData, isEditMode]);

    // Validate if form is valid
    const isFormValid = (): boolean => {
        const isShortCodeValid = state.ShortCode.length === SHORT_CODE_LENGTH && !isShortCodeDuplicate;
        const isProductSKUValid = state.ProductSKU.length > 0;
        const isProductNameValid = state.ProductName.length > 2;
        const isSuccessCutSKUValid = state.SuccessCutSKU.length > 0;
        const isWarrantyCutSKUValid = state.WarrantyCutSKU.length > 0;
        const isJobNameValid = Boolean(state.JobName);

        return (
            isShortCodeValid && isProductSKUValid && isProductNameValid && isSuccessCutSKUValid && isWarrantyCutSKUValid && !missingCutProfile && isJobNameValid
        );
    };

    const draft: [DialogState, React.Dispatch<React.SetStateAction<DialogState>>] = [state, setState];

    // Handle short code change with validation
    const handleShortCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const sanitized = sanitizeShortCode(e.target.value);
        setState((prev) => ({ ...prev, ShortCode: sanitized }));
    };

    // Handle product selection from grouped dropdown
    const handleProductSelect = (productSKU: string) => {
        setState((prev) => ({ ...prev, ProductSKU: productSKU }));
    };

    // BC data for the selected product (live from Business Central)
    const bcProduct = useMemo(
        () => productsData?.products.find((p) => p.ProductSKU === state.ProductSKU),
        [productsData, state.ProductSKU]
    );

    // Detect fields where saved table data differs from live BC data
    const bcDiffs = useMemo(() => {
        if (!isEditMode || !existingProduct || !bcProduct) return {} as Record<string, boolean>;
        return {
            ProductName: existingProduct.ProductName.trim() !== bcProduct.ProductName.trim(),
            ProductLine: existingProduct.ProductLine.trim() !== bcProduct.ProductLine.trim(),
            InvoiceName: existingProduct.InvoiceName.trim() !== bcProduct.InvoiceName.trim(),
            CutProfile: (existingProduct.CutProfile == null && bcProduct.CutProfile == null)
                ? false
                : Number(existingProduct.CutProfile) !== Number(bcProduct.CutProfile),
        };
    }, [isEditMode, existingProduct, bcProduct]);

    const hasBcDiff = Object.values(bcDiffs).some(Boolean);

    // Style Skins (15-x) have no BOM — CutProfile is set directly as a numeric
    const isStyleSkins = state.ProductSKU.startsWith('15-');

    // CutProfile is required — warn if BC resolves to null (unmapped 03- SKU or missing BOM entirely)
    // Skip for Style Skins since they don't use BOMs
    const missingCutProfile = !isStyleSkins && bcProduct != null && bcProduct.CutProfile == null;
    const hasUnmappedSKU = !isStyleSkins && bcProduct?.CutProfileSKU != null && bcProduct.CutProfile == null;

    const diffBorder = (field: keyof typeof bcDiffs) =>
        bcDiffs[field] ? colors.success.main : undefined;

    const diffHelper = (field: keyof typeof bcDiffs, bcValue: string) =>
        bcDiffs[field] ? `BC: ${bcValue} — saving will update` : undefined;

    const getCutProfileDisplay = (cutProfile: number | null, sku?: string | null): string => {
        if (cutProfile == null) return '—';
        const skuSuffix = sku ? ` (${sku})` : '';
        return `${cutProfile}${skuSuffix}`;
    };

    const pageTitle = isEditMode ? 'Edit Product Catalog' : 'Add Product Catalog';

    return (
        <EntityPageLayout loading={isLoading && !state.ProductSKU && isEditMode}>
            {/* Header */}
            <EntityPageHeader
                entityType='system'
                title={pageTitle}
                icon={<InventoryIcon />}
                isCreate={!isEditMode}
                loading={isLoading && !state.ProductSKU && isEditMode}
                onCancel={onClose}
                onSave={handleSubmit}
                saveDisabled={!isFormValid()}
                saving={isLoading}
            />

            {/* Product Details Card */}
            <InfoCard title='Product Details' icon={<InfoOutlinedIcon />}>
                <Grid container spacing={2.5}>
                    {/* Short Code Input - PIN style */}
                    <Grid item xs={12} sm={6}>
                        <Box>
                            <Typography
                                component='label'
                                variant='body2'
                                sx={{
                                    display: 'block',
                                    mb: '6px',
                                    fontWeight: typographyTokens.fontWeight.medium,
                                    color: colors.neutral[700],
                                    fontSize: typographyTokens.fontSize.sm,
                                    lineHeight: 1.43,
                                }}>
                                Short Code <span style={{ color: colors.error.main }}>*</span>
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <input
                                    type='text'
                                    value={state.ShortCode}
                                    onChange={handleShortCodeChange}
                                    onBlur={() => setShortCodeTouched(true)}
                                    disabled={isEditMode}
                                    placeholder='XX'
                                    maxLength={SHORT_CODE_LENGTH}
                                    style={{
                                        width: 80,
                                        height: 40,
                                        padding: '0 12px',
                                        fontFamily: typographyTokens.fontFamily.mono,
                                        fontSize: typographyTokens.fontSize.base,
                                        fontWeight: typographyTokens.fontWeight.bold,
                                        letterSpacing: '0.15em',
                                        textAlign: 'center',
                                        textTransform: 'uppercase',
                                        border: `1px solid ${shortCodeError && shortCodeTouched ? colors.error.main : colors.neutral[300]}`,
                                        borderRadius: `${radii.md}px`,
                                        backgroundColor: isEditMode ? colors.neutral[100] : colors.neutral[0],
                                        outline: 'none',
                                        boxSizing: 'border-box',
                                    }}
                                />
                                {!isEditMode && (
                                    <Button
                                        variant='secondary'
                                        size='small'
                                        onClick={() => {
                                            const chars = VALID_SHORT_CODE_CHARS.split('');
                                            const allCodes = chars.flatMap((a) => chars.map((b) => a + b));
                                            const nextCode = allCodes.find((code) => !existingShortCodes.includes(code));
                                            if (nextCode) {
                                                setState((prev) => ({ ...prev, ShortCode: nextCode }));
                                                setShortCodeTouched(true);
                                            }
                                        }}>
                                        Generate
                                    </Button>
                                )}
                            </Box>
                            <Typography
                                sx={{
                                    fontSize: typographyTokens.fontSize.xs,
                                    color: shortCodeError && shortCodeTouched ? colors.error.main : colors.neutral[500],
                                    mt: 0.5,
                                }}>
                                {shortCodeError && shortCodeTouched ? shortCodeError : 'Valid: 0-9, A-H, J-N, P-R, T-Z'}
                            </Typography>
                        </Box>
                    </Grid>

                    {/* Product Dropdown - Grouped by Product Line */}
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography
                                component='label'
                                sx={{
                                    fontSize: typographyTokens.fontSize.sm,
                                    fontWeight: typographyTokens.fontWeight.semibold,
                                    color: colors.neutral[700],
                                    mb: 0.5,
                                }}>
                                Product <span style={{ color: colors.error.main }}>*</span>
                            </Typography>
                            <FormControl fullWidth disabled={isEditMode} size='small'>
                                <Select
                                    value={state.ProductSKU}
                                    onChange={(e) => handleProductSelect(e.target.value)}
                                    displayEmpty
                                    MenuProps={{
                                        PaperProps: {
                                            sx: { maxHeight: 400 },
                                        },
                                    }}
                                    sx={{
                                        backgroundColor: isEditMode ? colors.neutral[100] : colors.neutral[0],
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: colors.neutral[300],
                                        },
                                    }}>
                                    <MenuItem value='' disabled>
                                        <Typography sx={{ color: colors.neutral[400] }}>Select a product...</Typography>
                                    </MenuItem>
                                    {PRODUCT_LINE_ORDER.map((line) => {
                                        const products = groupedProductOptions[line] || [];
                                        if (products.length === 0) return null;

                                        const lineColor = PRODUCT_LINE_COLORS[line] || {
                                            bg: colors.neutral[100],
                                            text: colors.neutral[700],
                                            border: colors.neutral[300],
                                        };

                                        return [
                                            <ListSubheader
                                                key={`header-${line}`}
                                                sx={{
                                                    backgroundColor: lineColor.bg,
                                                    color: lineColor.text,
                                                    fontWeight: typographyTokens.fontWeight.semibold,
                                                    borderLeft: `4px solid ${lineColor.border}`,
                                                    lineHeight: '36px',
                                                }}>
                                                {line}
                                            </ListSubheader>,
                                            ...products.map((product) => (
                                                <MenuItem
                                                    key={product.ProductSKU}
                                                    value={product.ProductSKU}
                                                    sx={{
                                                        pl: 3,
                                                        borderLeft: '4px solid transparent',
                                                        '&:hover': {
                                                            borderLeftColor: lineColor.border,
                                                        },
                                                    }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                        <Typography
                                                            sx={{
                                                                fontSize: typographyTokens.fontSize.sm,
                                                                fontWeight: typographyTokens.fontWeight.medium,
                                                            }}>
                                                            {product.ProductName}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize: typographyTokens.fontSize.xs,
                                                                color: colors.neutral[500],
                                                                fontFamily: typographyTokens.fontFamily.mono,
                                                            }}>
                                                            {product.ProductSKU}
                                                        </Typography>
                                                    </Box>
                                                </MenuItem>
                                            )),
                                        ];
                                    })}
                                    {/* Other products not in main lines */}
                                    {groupedProductOptions['Other']?.length > 0 && [
                                        <ListSubheader
                                            key='header-other'
                                            sx={{
                                                backgroundColor: colors.neutral[100],
                                                color: colors.neutral[700],
                                                fontWeight: typographyTokens.fontWeight.semibold,
                                                lineHeight: '36px',
                                            }}>
                                            Other
                                        </ListSubheader>,
                                        ...groupedProductOptions['Other'].map((product) => (
                                            <MenuItem key={product.ProductSKU} value={product.ProductSKU} sx={{ pl: 3 }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: typographyTokens.fontSize.sm,
                                                            fontWeight: typographyTokens.fontWeight.medium,
                                                        }}>
                                                        {product.ProductName}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontSize: typographyTokens.fontSize.xs,
                                                            color: colors.neutral[500],
                                                            fontFamily: typographyTokens.fontFamily.mono,
                                                        }}>
                                                        {product.ProductSKU}
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        )),
                                    ]}
                                </Select>
                                {isEditMode && (
                                    <FormHelperText sx={{ color: colors.neutral[500] }}>
                                        Product cannot be changed when editing
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Box>
                    </Grid>

                    {/* Read-only fields — show saved value, green helper shows BC value when different */}
                    <ReadOnlyField
                        size={6}
                        label='Product Line'
                        value={state.ProductLine || '—'}
                        borderColor={diffBorder('ProductLine')}
                        helperText={diffHelper('ProductLine', bcProduct?.ProductLine ?? '—')}
                    />
                    <ReadOnlyField
                        size={6}
                        label='Product Name'
                        value={state.ProductName || '—'}
                        borderColor={diffBorder('ProductName')}
                        helperText={diffHelper('ProductName', bcProduct?.ProductName ?? '—')}
                    />
                    <ReadOnlyField
                        size={6}
                        label='Invoice Name'
                        value={state.InvoiceName || '—'}
                        borderColor={diffBorder('InvoiceName')}
                        helperText={diffHelper('InvoiceName', bcProduct?.InvoiceName ?? '—')}
                    />
                    <ReadOnlyField
                        size={6}
                        label='Sheet Name'
                        value={bcProduct?.SheetName || '—'}
                    />

                    {/* Cut Profile — show 03- SKU, derived from numeric or BC */}
                    <ReadOnlyField
                        size={6}
                        label='Cut Profile'
                        value={getCutProfileDisplay(state.CutProfile, bcProduct?.CutProfileSKU)}
                        borderColor={missingCutProfile ? colors.error.main : diffBorder('CutProfile')}
                        warning={
                            hasUnmappedSKU
                                ? `No cut profile mapping for ${bcProduct?.CutProfileSKU}`
                                : missingCutProfile
                                    ? 'No cut profile found — check BOM in Business Central'
                                    : undefined
                        }
                        helperText={
                            missingCutProfile
                                ? undefined
                                : diffHelper('CutProfile', getCutProfileDisplay(bcProduct?.CutProfile ?? null, bcProduct?.CutProfileSKU))
                        }
                    />

                    <DraftSelectField
                        size={6}
                        label='Success Cut SKU'
                        stateKey='SuccessCutSKU'
                        draft={draft}
                        options={successSKUOptionsForm}
                        defaultFirst={false}
                        required
                    />

                    <DraftSelectField
                        size={6}
                        label='Warranty Cut SKU'
                        stateKey='WarrantyCutSKU'
                        draft={draft}
                        options={warrantySKUOptionsForm}
                        defaultFirst={false}
                        required
                    />

                    <ReadOnlyField size={6} label='Cut Plan' value={CUT_FEE_MAP[state.CutPlanID as BillingType] || '—'} />

                    <DraftSelectField
                        size={6}
                        label='Status'
                        stateKey='Status'
                        draft={draft}
                        options={PRODUCT_CATALOG_STATUS_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
                        required
                    />

                    {/* Job Name — freeSolo autocomplete populated from existing values */}
                    <Grid item xs={12} sm={6}>
                        <Box>
                            <Typography
                                component='label'
                                variant='body2'
                                sx={{
                                    display: 'block',
                                    mb: '6px',
                                    fontWeight: typographyTokens.fontWeight.medium,
                                    color: colors.neutral[700],
                                    fontSize: typographyTokens.fontSize.sm,
                                    lineHeight: 1.43,
                                }}>
                                Printer Job Name
                            </Typography>
                            <Autocomplete
                                freeSolo
                                openOnFocus
                                forcePopupIcon
                                autoHighlight
                                size='small'
                                value={draft[0].JobName}
                                options={jobNameOptions}
                                onChange={(_, value) =>
                                    draft[1]((prev) => ({ ...prev, JobName: (value as string) ?? '' }))
                                }
                                onInputChange={(_, value, reason) => {
                                    if (reason !== 'reset') {
                                        draft[1]((prev) => ({ ...prev, JobName: value }));
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant='outlined'
                                        placeholder='Select or type a job name'
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                minHeight: 40,
                                                fontSize: typographyTokens.fontSize.base,
                                                backgroundColor: colors.neutral[0],
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: colors.neutral[300],
                                                    borderRadius: `${radii.md}px`,
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: colors.neutral[400],
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: colors.primary[500],
                                                    borderWidth: 2,
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    </Grid>
                </Grid>

                {/* Edit mode info banner */}
                {isEditMode && (
                    <Box
                        sx={{
                            mt: 3,
                            p: 2,
                            backgroundColor: hasBcDiff ? colors.success.light : colors.info.light,
                            borderRadius: `${radii.md}px`,
                            border: `1px solid ${hasBcDiff ? colors.success.main : colors.info.main}20`,
                        }}>
                        <Typography
                            sx={{
                                fontSize: typographyTokens.fontSize.sm,
                                color: hasBcDiff ? colors.success.dark : colors.info.dark,
                            }}>
                            {hasBcDiff
                                ? 'Fields highlighted in green have changed in Business Central. Saving will update the product catalog with the latest BC data.'
                                : 'When editing, you can modify the Success Cut SKU, Warranty Cut SKU, and Job Name. The Short Code, Product, and Cut Profile cannot be changed.'}
                        </Typography>
                    </Box>
                )}
            </InfoCard>
        </EntityPageLayout>
    );
};

// Keep old export name for backwards compatibility
export const MaterialProductEditPanel = ProductCatalogEditPanel;
